import { useEffect, useState } from 'react';

import { Icon, IconButton, Tooltip } from '@/ui';

import type { FC } from 'react';

import {
  FORTY_EIGHT_HOURS_MS,
  isWithinRescheduleWindow,
  MEMBER_RESCHEDULE_NOTICE,
  MEMBER_RESCHEDULE_STARTED_NOTICE,
} from '@/components/MentoCal/sessionTiming';

const MAX_TIMEOUT_MS = 2_147_483_647;

interface MemberSessionActionButtonsProps {
  actionContext: string;
  allowLateReschedule?: boolean;
  onCancel?: () => void;
  onReschedule?: () => void;
  rescheduleAllowed?: boolean;
  startedAt: string;
}

const MemberSessionActionButtons: FC<MemberSessionActionButtonsProps> = ({
  actionContext,
  allowLateReschedule = false,
  onCancel,
  onReschedule,
  rescheduleAllowed = true,
  startedAt,
}) => {
  const [clock, setClock] = useState(Date.now);
  const now = Math.max(clock, Date.now());
  const start = new Date(startedAt).getTime();
  const hasReschedule = Boolean(onReschedule);
  const rescheduleStarted = Number.isFinite(start) && start <= now;
  const rescheduleDisabled =
    rescheduleStarted ||
    (!allowLateReschedule && isWithinRescheduleWindow(startedAt, now));
  const showReschedule =
    hasReschedule && (rescheduleAllowed || rescheduleDisabled);
  const rescheduleDisabledNotice = rescheduleStarted
    ? MEMBER_RESCHEDULE_STARTED_NOTICE
    : MEMBER_RESCHEDULE_NOTICE;

  useEffect(() => {
    if (!hasReschedule || rescheduleDisabled) return undefined;

    const restrictionAt = allowLateReschedule
      ? start
      : start - FORTY_EIGHT_HOURS_MS;
    const remaining = restrictionAt - Date.now();
    if (!Number.isFinite(start) || remaining <= 0) return undefined;

    const timer = window.setTimeout(
      () => setClock(Date.now()),
      Math.min(remaining, MAX_TIMEOUT_MS),
    );
    return () => window.clearTimeout(timer);
  }, [
    allowLateReschedule,
    clock,
    hasReschedule,
    rescheduleDisabled,
    startedAt,
  ]);

  const handleReschedule = () => {
    const current = Date.now();
    if (
      (Number.isFinite(start) && start <= current) ||
      (!allowLateReschedule && isWithinRescheduleWindow(startedAt, current))
    ) {
      setClock(current);
      return;
    }
    onReschedule?.();
  };

  return (
    <div className="flex shrink-0 items-center gap-2 md:gap-6">
      {showReschedule && (
        <Tooltip
          title={rescheduleDisabled ? rescheduleDisabledNotice : 'Reschedule'}
        >
          <span
            className="inline-flex rounded ring-1 ring-inset ring-cement-300"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Disabled MUI buttons cannot receive focus, so focus the tooltip target.
            tabIndex={rescheduleDisabled ? 0 : undefined}
          >
            <IconButton
              aria-label={`Reschedule ${actionContext}`}
              className="!rounded"
              color="secondary"
              disabled={rescheduleDisabled}
              onClick={handleReschedule}
            >
              <Icon>edit_calendar</Icon>
            </IconButton>
          </span>
        </Tooltip>
      )}
      {onCancel && (
        <Tooltip title="Cancel">
          <span className="inline-flex rounded ring-1 ring-inset ring-cement-300">
            <IconButton
              aria-label={`Cancel ${actionContext}`}
              className="!rounded"
              color="secondary"
              onClick={onCancel}
            >
              <Icon>trash</Icon>
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default MemberSessionActionButtons;
